import React, { useEffect, useMemo, useState } from "react";
import { apiGet, apiPost } from "../../services/api";
import { PatientResult } from "../../components/doctor/PatientSearch";
import { ConsultantWorkOption } from "./types";
import { normalize } from "./utils";

interface ConsultantPayoutsProps {
    consultants: { id: number; name: string }[];
    consultantServiceFees: any[];
    consultantServices: any[];
    financeDate: string;
    showToast: (args: { message: string; type: "success" | "error" | "info" | "warning" }) => void;
    onSuccess: () => Promise<void>;
}

export default function ConsultantPayouts({ consultants, consultantServiceFees, consultantServices, financeDate, showToast, onSuccess }: ConsultantPayoutsProps) {
    const [consultantPayment, setConsultantPayment] = useState({
        consultantId: "",
        patientName: "",
        patientId: "",
        patientMobile: "",
        work: "",
        feeId: "",
        serviceId: "",
        amount: "",
        mode: "Cash",
    });
    const [payingConsultant, setPayingConsultant] = useState(false);
    const [consultantPatientResults, setConsultantPatientResults] = useState<PatientResult[]>([]);

    useEffect(() => {
        if (consultantPayment.patientId) {
            setConsultantPatientResults([]);
            return;
        }
        const q = consultantPayment.patientName.trim();
        if (q.length < 2) {
            setConsultantPatientResults([]);
            return;
        }

        const t = window.setTimeout(async () => {
            try {
                const res = await apiGet(`/api/patients/search?q=${encodeURIComponent(q)}`);
                setConsultantPatientResults(Array.isArray(res) ? (res as PatientResult[]) : []);
            } catch {
                setConsultantPatientResults([]);
            }
        }, 260);

        return () => window.clearTimeout(t);
    }, [consultantPayment.patientId, consultantPayment.patientName]);

    const consultantPaymentWorkOptions = useMemo<ConsultantWorkOption[]>(() => {
        const cid = Number(consultantPayment.consultantId);
        if (!cid) return [];

        const serviceById = new Map<number, any>();
        for (const service of consultantServices) {
            serviceById.set(Number(service.id), service);
        }

        const rows: ConsultantWorkOption[] = [];
        for (const fee of consultantServiceFees) {
            if (Number(fee.consultant_id) !== cid) continue;
            const sid = Number(fee.service_id);
            const service = serviceById.get(sid);
            if (!service) continue;
            const parsedFeeId = Number(fee.id);
            const feeRowId =
                fee.id === null || fee.id === undefined || fee.id === "" || !Number.isFinite(parsedFeeId) || parsedFeeId <= 0
                    ? null
                    : parsedFeeId;
            rows.push({
                key: `${feeRowId || 0}-${sid}-${Number(fee.procedure_master_id || service.procedure_master_id || 0)}`,
                feeRowId,
                serviceId: sid,
                serviceName: service.name,
                procedureName: service.procedure_name || null,
                fee: Number(fee.fee || 0),
            });
        }

        rows.sort((a, b) => {
            const na = normalize(`${a.serviceName} ${a.procedureName || ""}`);
            const nb = normalize(`${b.serviceName} ${b.procedureName || ""}`);
            return na.localeCompare(nb);
        });
        return rows;
    }, [consultantPayment.consultantId, consultantServiceFees, consultantServices]);

    const filteredConsultantPaymentWorks = useMemo(() => {
        const q = normalize(consultantPayment.work);
        if (!q) return consultantPaymentWorkOptions;
        return consultantPaymentWorkOptions.filter((work) =>
            normalize(`${work.serviceName} ${work.procedureName || ""}`).includes(q)
        );
    }, [consultantPayment.work, consultantPaymentWorkOptions]);

    const handlePayConsultant = async () => {
        const amt = Number(consultantPayment.amount);
        if (!consultantPayment.consultantId || !consultantPayment.patientId || !consultantPayment.serviceId) {
            showToast({ message: "Fill all required consultant payment fields.", type: "warning" });
            return;
        }
        if (!Number.isFinite(amt) || amt <= 0) {
            showToast({ message: "Enter valid payment amount.", type: "warning" });
            return;
        }

        setPayingConsultant(true);
        try {
            const payload: Record<string, unknown> = {
                consultant_id: consultantPayment.consultantId,
                patient_id: consultantPayment.patientId,
                amount: amt,
                payment_mode: consultantPayment.mode || "Cash",
                paid_date: financeDate,
                notes: `CareFlow direct: ${consultantPayment.work}`,
            };

            if (consultantPayment.feeId) {
                payload.fee_id = Number(consultantPayment.feeId);
            }

            await apiPost("/api/manager/consultants/pay", payload);

            showToast({ message: "Consultant payment saved.", type: "success" });
            setConsultantPayment((prev) => ({
                ...prev,
                patientId: "",
                patientName: "",
                patientMobile: "",
                work: "",
                feeId: "",
                serviceId: "",
                amount: "",
            }));
            await onSuccess();
        } catch {
            showToast({ message: "Failed to record consultant payment.", type: "error" });
        } finally {
            setPayingConsultant(false);
        }
    };

    const inputStyle = { width: '100%', padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.9rem', background: '#f8fafc' };

    return (
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '0.85rem', fontWeight: 700 }}>Consultant Name</label>
                    <select style={inputStyle} value={consultantPayment.consultantId} onChange={(e) => setConsultantPayment((prev) => ({ ...prev, consultantId: e.target.value, feeId: "", serviceId: "", work: "", amount: "" }))}>
                        <option value="">Select consultant</option>
                        {consultants.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                    </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', position: 'relative' }}>
                    <label style={{ fontSize: '0.85rem', fontWeight: 700 }}>Patient Filter</label>
                    <input style={inputStyle} value={consultantPayment.patientId ? `${consultantPayment.patientName} (${consultantPayment.patientMobile})` : consultantPayment.patientName} onChange={(e) => setConsultantPayment((p) => ({ ...p, patientName: e.target.value, patientId: "", patientMobile: "" }))} placeholder="Patient Name / Mobile" />
                    {!consultantPayment.patientId && consultantPatientResults.length > 0 && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #cbd5e1', zIndex: 10, borderRadius: '8px', boxShadow: 'var(--mod-shadow-md)' }}>
                            {consultantPatientResults.map((pr) => (
                                <div key={pr.patient_id} style={{ padding: '10px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }} onClick={() => setConsultantPayment((p) => ({ ...p, patientId: String(pr.patient_id), patientName: pr.name, patientMobile: pr.mobile || "" }))}>
                                    <div style={{ fontWeight: 600 }}>{pr.name} <span style={{ color: '#64748b', fontSize: '0.8rem' }}>#{pr.patient_id}</span></div>
                                    <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{pr.mobile}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', position: 'relative' }}>
                    <label style={{ fontSize: '0.85rem', fontWeight: 700 }}>Service / Procedure</label>
                    <input style={inputStyle} value={consultantPayment.work} onChange={(e) => setConsultantPayment((p) => ({ ...p, work: e.target.value, feeId: "", serviceId: "" }))} placeholder="Search fee registry" disabled={!consultantPayment.consultantId} />
                    {!consultantPayment.serviceId && filteredConsultantPaymentWorks.length > 0 && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #cbd5e1', zIndex: 10, borderRadius: '8px', boxShadow: 'var(--mod-shadow-md)', maxHeight: '200px', overflowY: 'auto' }}>
                            {filteredConsultantPaymentWorks.map((work) => (
                                <div key={work.key} style={{ padding: '10px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between' }} onClick={() => setConsultantPayment((p) => ({ ...p, work: work.procedureName ? `${work.serviceName} (${work.procedureName})` : work.serviceName, feeId: work.feeRowId ? String(work.feeRowId) : "", serviceId: String(work.serviceId), amount: String(work.fee) }))}>
                                    <span>{work.procedureName ? `${work.serviceName} (${work.procedureName})` : work.serviceName}</span>
                                    <strong style={{ color: '#0369a1' }}>₹{work.fee}</strong>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <label style={{ fontSize: '0.85rem', fontWeight: 700 }}>Amount (₹)</label>
                        <input style={inputStyle} type="number" value={consultantPayment.amount} onChange={(e) => setConsultantPayment((p) => ({ ...p, amount: e.target.value }))} />
                    </div>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <label style={{ fontSize: '0.85rem', fontWeight: 700 }}>Mode</label>
                        <select style={inputStyle} value={consultantPayment.mode} onChange={(e) => setConsultantPayment((p) => ({ ...p, mode: e.target.value }))}>
                            <option value="Cash">Cash</option><option value="UPI">UPI / GPay</option><option value="Card">Card</option><option value="Bank Transfer">Bank</option>
                        </select>
                    </div>
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button style={{ padding: '10px 24px', borderRadius: '8px', background: 'linear-gradient(135deg, var(--mod-sky), #3b82f6)', color: '#fff', border: 'none', fontWeight: 700, cursor: (payingConsultant || !consultantPayment.consultantId || !consultantPayment.patientId) ? 'not-allowed' : 'pointer', opacity: (payingConsultant || !consultantPayment.consultantId || !consultantPayment.patientId) ? 0.6 : 1 }} onClick={() => void handlePayConsultant()}>
                    {payingConsultant ? "Saving..." : "Record Consultant Payment"}
                </button>
            </div>
        </div>
    );
}
